// @flow

import { useContext, useEffect, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { selectCurrentId } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';

import { selectCurrency } from 'src/Layout/modules/selectors';
import { Select, type Props as FieldProps } from 'src/Reshaped/Form/Select';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectShopCurrencies } from 'src/modules/app/selectors/currency';

import ContactFormContext from '../ContactFormContext';

type Props = {
  ...Partial<FieldProps>,
  name: string,
  placeholder?: string,
};

export function CurrencySelect({
  disabled,
  loading,
  onChange,
  name,
  label,
  help,
  placeholder,
  rules,
  control,
}: Props): React.MixedElement {
  const { formatCurrency, __ } = useTranslator();
  const { setValue, getValues, watch } = useFormContext();

  const country = watch('country');

  const { orgId } = useContext(ContactFormContext);

  const currencyForm = watch('currency');

  const sharingId = useAppSelector(selectCurrentId);

  const currencies = useAppSelector(state =>
    selectShopCurrencies(state, {
      country: country || '',
      sharingId: orgId,
    }),
  );

  const currencyProps = useMemo(
    () => ({
      country: country || '',
      orgId: orgId || sharingId || '',
    }),
    [country, orgId, sharingId],
  );

  const defaultCurrency = useAppSelector(state =>
    selectCurrency(state, currencyProps),
  );

  // Update to first render defaultCurrency and add dirty
  useEffect(() => {
    if (defaultCurrency && !currencyForm) {
      setValue(name, defaultCurrency, { shouldDirty: true });
    }
  }, [defaultCurrency, country, name, setValue, currencyForm]);

  // update currency when country or available currencies change
  useEffect(() => {
    const currentValue = getValues(name);
    if (!currencies.includes(currentValue)) {
      setValue(name, currencies[0] || '');
    }
  }, [currencies, getValues, name, setValue]);

  const options = country
    ? currencies.map(currency => {
        const isRepetitive =
          formatCurrency(currency).localeCompare(currency) === 0;

        return {
          label: isRepetitive
            ? currency
            : `${formatCurrency(currency)} ${currency}`,
          value: currency,
        };
      })
    : // Even when the country is not selected,
      // currencies are filled with the USD one.
      // We prefer to have no options in this case..
      [];
  return (
    <Select
      name={name}
      label={label}
      options={options}
      control={control}
      placeholder={country ? placeholder : __('Select a country first')}
      onChange={e => {
        // update the form field
        setValue(name, e.value);
        // update the parent for chain changes
        if (typeof onChange === 'function') onChange(e);
      }}
      // TODO: How to represent the loading state (reshaped)
      // isLoading={loading}
      disabled={disabled || loading || !country}
      // TODO: Check how to apply readonly (reshaped)
      // isReadonly={!isDisabled && options.length <= 1}
      rules={rules}
      help={help}
    />
  );
}
